import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DefaultErrorFallback } from '~/components/ErrorBoundary/DefaultErrorFallback'
import Loading from '~/components/Loading'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import { telemetryClient } from '~/lib/telemetry'

import { getCreativeStudioUrl } from '../../../services/creative-studio'
import { isSafeCreativeStudioEmbedUrl } from './helpers'

import * as S from './styles'

const CreativeStudioPage = () => {
  const { t } = useAPPTranslation()
  const { i18n } = useTranslation()
  const [iframeUrl, setIframeUrl] = useState('')
  const [error, setError] = useState<Error | null>(null)

  const hasError = useMemo(() => !!error, [error])

  const fetchCreativeStudioUrl = useCallback(() => {
    setError(null)
    setIframeUrl('')

    getCreativeStudioUrl(i18n.language)
      .then(({ data }) => {
        if (!isSafeCreativeStudioEmbedUrl(data.url)) {
          const message = '[creative-studio] Invalid creative studio embed URL'
          const payload = { url: data.url }
          console.error(message, payload)
          telemetryClient.logError({ message, body: JSON.stringify(payload) })
          setError(new Error('Invalid creative studio embed URL'))
          return
        }

        setIframeUrl(data.url)
      })
      .catch(err => {
        const errorInstance =
          err instanceof Error ? err : new Error(String(err))
        const message = `[creative-studio] ${errorInstance.message}`
        const payload = { error: String(err) }
        console.error(message, payload)
        telemetryClient.logError({ message, body: JSON.stringify(payload) })
        setError(errorInstance)
      })
  }, [i18n.language])

  useEffect(() => {
    fetchCreativeStudioUrl()
  }, [fetchCreativeStudioUrl])

  const errorFallbackProps = useMemo(
    () => ({
      title: t('c:components.ErrorBoundary.title'),
      description: t('c:components.ErrorBoundary.description'),
      tryAgainLabel: t('c:components.ErrorBoundary.tryAgainLabel'),
      errorLabel: t('c:components.ErrorBoundary.errorLabel'),
      fullScreen: false
    }),
    [t]
  )

  if (hasError) {
    return (
      <DefaultErrorFallback
        error={error}
        reset={fetchCreativeStudioUrl}
        {...errorFallbackProps}
      />
    )
  }

  if (!iframeUrl) return <Loading status>{t('rm:Loading')}</Loading>

  return (
    <S.Container>
      <iframe
        src={iframeUrl}
        title="creative-studio"
        referrerPolicy="strict-origin-when-cross-origin"
        allow="fullscreen"
      />
    </S.Container>
  )
}

export default CreativeStudioPage
